import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  Signal, RefreshCw, Trophy, Target,
  CheckCircle, ArrowLeft, Filter,
  BarChart3, AlertTriangle, ArrowRight, Zap
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SignalHistoryPage = () => {
  const { user } = useAuth();
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, tp1, tp2, tp3, stopped
  const [symbolFilter, setSymbolFilter] = useState('all');

  const fetchSignals = async () => {
    setLoading(true);
    try {
      // Use the new performance API for closed signals
      const response = await axios.get(`${API_URL}/api/signals/performance`);
      const data = response.data;
      
      if (data.status === 'ok') {
        setStats({
          total: data.total_signals,
          tp1Rate: data.tp1_rate,
          tp2Rate: data.tp2_rate,
          tp3Rate: data.tp3_rate,
          stopRate: data.stop_rate,
          winRate: data.win_rate,
          avgProfit: data.avg_profit,
          avgLoss: data.avg_loss,
          avgRR: data.avg_rr,
          platformLaunch: data.platform_launch
        });
        setSignals(data.signals || []);
      }
    } catch (err) {
      console.error('Signals fetch error:', err);
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignals();
  }, []);

  // Calculate statistics from stats or signals
  const totalSignals = stats?.total || signals.length;
  const tp1HitSignals = signals.filter(s => s.tp1_hit);
  const tp2HitSignals = signals.filter(s => s.tp2_hit);
  const tp3HitSignals = signals.filter(s => s.tp3_hit);
  const stoppedSignals = signals.filter(s => s.outcome_status === 'stopped' || s.sl_hit);
  
  const tp1Rate = stats?.tp1Rate ?? 0;
  const tp2Rate = stats?.tp2Rate ?? 0;
  const tp3Rate = stats?.tp3Rate ?? 0;
  const stopRate = stats?.stopRate ?? 0;

  // Average R/R from API - show '-' if no data
  const avgRR = stats?.avgRR && stats.avgRR > 0 ? stats.avgRR.toFixed(1) : '-';

  // Get unique symbols for filter
  const symbols = [...new Set(signals.map(s => s.symbol))];

  // Apply filters
  const filteredSignals = signals.filter(s => {
    // Outcome filter
    if (filter === 'tp1' && !s.tp1_hit) return false;
    if (filter === 'tp2' && !s.tp2_hit) return false;
    if (filter === 'tp3' && !s.tp3_hit) return false;
    if (filter === 'stopped' && s.outcome_status !== 'stopped' && s.status !== 'sl_hit') return false;
    
    // Symbol filter
    if (symbolFilter !== 'all' && s.symbol !== symbolFilter) return false;
    
    return true;
  });

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: price > 100 ? 0 : 2
    }).format(price);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getOutcomeLabel = (signal) => {
    if (signal.tp3_hit) return { label: 'TP3', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    if (signal.tp2_hit) return { label: 'TP2', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (signal.tp1_hit) return { label: 'TP1', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    if (signal.outcome_status === 'stopped' || signal.status === 'sl_hit') return { label: 'STOP', color: 'text-red-400', bg: 'bg-red-500/20' };
    return { label: '-', color: 'text-slate-400', bg: 'bg-slate-500/20' };
  };

  return (
    <div className="min-h-screen bg-brand-dark py-6">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/signals">
              <Button variant="outline" size="sm" className="border-slate-700">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Aktif Sinyaller
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-purple-400" />
                Geçmiş Sinyaller
              </h1>
              <p className="text-sm text-slate-400">Kapanmış sinyaller ve performans istatistikleri</p>
            </div>
          </div>
          <Button 
            onClick={() => fetchSignals()} 
            variant="outline" 
            size="sm"
            disabled={loading}
            className="border-slate-700"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
        </div>

        {/* Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="border-slate-700/50 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Performans Özeti</h2>
                <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                  Tamamlanmış: {signals.length || 0} Sinyal
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-900/20 rounded-lg border border-green-500/20">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span className="text-xs text-green-400">TP1</span>
                  </div>
                  <p className="text-2xl font-bold text-green-400">%{tp1Rate}</p>
                  <p className="text-[10px] text-slate-400">En az 1 hedef</p>
                </div>
                
                <div className="text-center p-4 bg-amber-900/20 rounded-lg border border-amber-500/20">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Target className="h-4 w-4 text-amber-400" />
                    <span className="text-xs text-amber-400">TP2</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-400">%{tp2Rate}</p>
                  <p className="text-[10px] text-slate-400">Ana hedef</p>
                </div>
                
                <div className="text-center p-4 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Trophy className="h-4 w-4 text-emerald-400" />
                    <span className="text-xs text-emerald-400">TP3</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-400">%{tp3Rate}</p>
                  <p className="text-[10px] text-slate-400">Tam hedef</p>
                </div>
                
                <div className="text-center p-4 bg-purple-900/20 rounded-lg border border-purple-500/20">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <BarChart3 className="h-4 w-4 text-purple-400" />
                    <span className="text-xs text-purple-400">Risk/Kazanç</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-400">{avgRR !== '-' ? `1:${avgRR}` : '-'}</p>
                  <p className="text-[10px] text-slate-400">Ortalama</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* CTA for non-logged in users */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <Card className="border-green-500/30 bg-gradient-to-r from-green-900/20 via-emerald-900/10 to-green-900/20 overflow-hidden">
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-center sm:text-left">
                    <div className="hidden sm:flex h-10 w-10 rounded-full bg-green-500/20 items-center justify-center shrink-0">
                      <Zap className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Bu sonuçları üreten canlı sinyalleri görmek ister misin?</p>
                      <p className="text-sm text-slate-400">Ücretsiz üye ol, anlık bildirimleri al</p>
                    </div>
                  </div>
                  <Link to="/pricing">
                    <Button className="bg-green-600 hover:bg-green-700 text-white font-bold px-6 whitespace-nowrap">
                      Canlı Sinyalleri Gör <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px] bg-slate-900 border-slate-700">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Sonuç" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="tp1">TP1 Gören</SelectItem>
              <SelectItem value="tp2">TP2 Ulaşan</SelectItem>
              <SelectItem value="tp3">TP3 Yapan</SelectItem>
              <SelectItem value="stopped">Stop ile Sonuçlanan</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={symbolFilter} onValueChange={setSymbolFilter}>
            <SelectTrigger className="w-[150px] bg-slate-900 border-slate-700">
              <SelectValue placeholder="Parite" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Pariteler</SelectItem>
              {symbols.map(symbol => (
                <SelectItem key={symbol} value={symbol}>{symbol}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Signals Table */}
        <Card className="border-slate-700/50 bg-slate-900/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="text-left p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Parite</th>
                  <th className="text-left p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">İşlem</th>
                  <th className="text-left p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Sonuç</th>
                  <th className="text-right p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">K/Z</th>
                  <th className="text-right p-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Açılış Tarihi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center">
                      <RefreshCw className="h-6 w-6 text-slate-400 animate-spin mx-auto mb-2" />
                      <p className="text-slate-400">Yükleniyor...</p>
                    </td>
                  </tr>
                ) : filteredSignals.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center">
                      <Signal className="h-8 w-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-400">Geçmiş sinyal bulunamadı</p>
                    </td>
                  </tr>
                ) : (
                  filteredSignals.map((signal) => {
                    const outcome = getOutcomeLabel(signal);
                    return (
                      <tr key={signal.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="p-4">
                          <span className="font-mono font-semibold text-white">{signal.symbol}</span>
                        </td>
                        <td className="p-4">
                          <Badge className={`text-xs ${
                            signal.action === 'BUY' ? 'bg-green-900/50 text-green-400 border-green-700/50' : 
                            signal.action === 'EXIT' ? 'bg-amber-900/50 text-amber-400 border-amber-700/50' :
                            'bg-red-900/50 text-red-400 border-red-700/50'
                          }`}>
                            {signal.action === 'BUY' ? 'AL' : signal.action === 'EXIT' ? 'ÇIKIŞ' : 'SAT'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <Badge className={`${outcome.bg} ${outcome.color} border-transparent`}>
                            {outcome.label}
                          </Badge>
                        </td>
                        <td className="p-4 text-right">
                          {(() => {
                            let pnl = signal.pnl_percent;
                            
                            // Fallback: Eğer pnl_percent yoksa, SL/TP'den hesapla
                            if (pnl === undefined || pnl === null) {
                              const entry = parseFloat(signal.entry_price);
                              const sl = parseFloat(signal.stop_loss);
                              const tp1 = parseFloat(signal.tp1);
                              const isBuy = signal.action === 'BUY';
                              
                              if (entry && sl && (signal.sl_hit || signal.outcome_status === 'stopped')) {
                                // Stop olmuş - SL fiyatından hesapla
                                pnl = isBuy 
                                  ? ((sl - entry) / entry) * 100 
                                  : ((entry - sl) / entry) * 100;
                              } else if (entry && tp1 && signal.tp1_hit) {
                                // TP1 vurmuş
                                pnl = isBuy 
                                  ? ((tp1 - entry) / entry) * 100 
                                  : ((entry - tp1) / entry) * 100;
                              }
                            }
                            
                            if (pnl !== undefined && pnl !== null && !isNaN(pnl)) {
                              return (
                                <span className={`font-mono font-semibold ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)}%
                                </span>
                              );
                            }
                            return <span className="text-slate-500">-</span>;
                          })()}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex flex-col items-end">
                            <span className="text-sm text-slate-300">
                              {formatDateTime(signal.created_at || signal.timestamp)}
                            </span>
                            {signal.closed_at && (
                              <span className="text-xs text-slate-500 mt-0.5">
                                Kapanış: {formatDateTime(signal.closed_at)}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-400">
              <strong className="text-slate-300">Yasal Uyarı:</strong> Bu istatistikler Borsamex algoritmaları tarafından üretilmiştir. 
              Geçmiş performans, gelecekteki sonuçların garantisi değildir. Yatırım kararlarınızı kendi araştırmanıza dayandırın.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignalHistoryPage;
